// [PURPOSE]: Determine whether a character meets the attunement requirement of
//            a magic item, honoring Artificer Magic Item Savant's L14 bypass.
//
// [WHY]: RAW DMG p.138 — some magic items have attunement requirements like
//        "by a wizard" or "by a creature of good alignment". Pre-fix, the app
//        had item data carrying `attunementRequirement: 'spellcaster'` etc.
//        but NO consumer ever checked it. Q26 finding (TCoE p.12) flagged
//        that Magic Item Savant should let Artificer L14+ bypass such reqs,
//        but the bypass was meaningless because the gate didn't exist.
//
//        STRUCT-C wiring 2 (2026-05-16): add the gate + the bypass in the
//        same commit per Rule 25 (Rule of Expansion — same-commit consumer).
//
// [INTEGRATION]: Imported by InventoryContext.attuneToItem.

import { effectCalculator } from '../services/effectCalculator';

/**
 * Returns true if the character satisfies the magic item's
 * `attunementRequirement`, OR if the character has the Magic Item Savant
 * ignore-reqs flag (Artificer L14+).
 *
 * Recognized requirement strings (case-insensitive):
 *   - 'spellcaster'  → character has at least one spellcasting class
 *   - '<className>'  → character has at least one level in that class
 *     (e.g., 'wizard', 'cleric', 'druid', etc.)
 *   - '<className1>,<className2>,...'  → any matching class works
 *   - undefined / null / '' → no requirement; always passes
 *
 * Fail-safe: unrecognized requirement strings (e.g., a future 'good_alignment'
 * or 'attuned_by_a_dragon') return FALSE — they block attunement until the
 * helper is extended to evaluate them. This is the opposite of Q9
 * effectRegistry's `_matchesStringCondition` which preserves accept on
 * unknown PREFIXES; the difference is that prefixes are structured
 * ('class:foo', 'race:bar') while attunementRequirement values are bare
 * tokens. Blocking on unknown tokens prevents silent leakage.
 *
 * @param {object} playerStats - Character data with `.classes` array
 * @param {Array}  inventory   - Inventory (for effect-context flag lookup)
 * @param {string|undefined} requirement - The attunementRequirement string
 * @returns {boolean} True if the character can attune.
 */
export function meetsAttunementRequirement(playerStats, inventory, requirement) {
  if (!requirement || typeof requirement !== 'string') return true;
  if (!playerStats || !Array.isArray(playerStats.classes)) return false;

  // Artificer Magic Item Savant bypass — RAW TCoE p.12 ignores ALL class/
  // race/spell/level requirements
  const hasIgnoreReqsFlag = effectCalculator.hasFeatureFlag(
    playerStats, inventory || [], 'artificer_magic_item_savant_ignore_reqs'
  );
  if (hasIgnoreReqsFlag) return true;

  const req = requirement.trim().toLowerCase();
  if (req.length === 0) return true;

  // 'spellcaster' → any class with a spellcasting block
  if (req === 'spellcaster') {
    return playerStats.classes.some(c => {
      const className = String(c?.className || '').toLowerCase();
      // Known full/half/third casters at the base-class level
      return [
        'bard', 'cleric', 'druid', 'paladin', 'ranger',
        'sorcerer', 'warlock', 'wizard', 'artificer',
      ].includes(className);
    });
  }

  // Class-name list (single or comma-separated)
  const allowedClasses = req
    .split(',')
    .map(name => name.trim())
    .filter(name => name.length > 0);

  return playerStats.classes.some(c => {
    const characterClassName = String(c?.className || '').toLowerCase();
    return characterClassName.length > 0 && allowedClasses.includes(characterClassName);
  });
}
